import React from 'react';
import { graphql, useFragment } from 'react-relay';

import { BrandMessageTemplateUpdateDialog_brandMessageTemplate$key } from '../../../relay/__generated__/BrandMessageTemplateUpdateDialog_brandMessageTemplate.graphql';
import { BrandMessageTemplateUpdateDialog_brandMessageTemplateUpdateMutation } from '../../../relay/__generated__/BrandMessageTemplateUpdateDialog_brandMessageTemplateUpdateMutation.graphql';
import Dialog, { DialogProps } from '../../core/Dialog';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import Text from '../../core/Text';
import BrandMessageTemplateCreateDialog, {
  BrandMessageTemplateForm,
  BrandMessageTemplateFormValues,
  initialValuesForType,
  normalizeBrandMessageTemplateInput,
  validateBrandMessageTemplate,
} from '../BrandMessageTemplateCreateDialog';
import { BrandMessageType } from '../constants';

const BrandMessageTemplateUpdateDialog_brandMessageTemplate = graphql`
  fragment BrandMessageTemplateUpdateDialog_brandMessageTemplate on BrandMessageTemplate {
    templateCode
    templateName
    chatBubbleType
    content
    adult
    imageUrl
    imageLink
    header
    additionalContent
    carousel
    wideItemList
    video
    commerce
    buttons {
      name
      linkType
      linkIos
      linkMobile
      linkPc
      linkAndroid
      bizFormId
      ordering
    }
    coupon {
      description
      linkAndroid
      linkIos
      linkMobile
      linkPc
      title
    }
  }
`;

type TemplateData = {
  templateCode: string;
  templateName: string;
  chatBubbleType: string;
  content: string;
  adult: boolean;
  imageUrl?: string | null;
  imageLink?: string | null;
  header?: string | null;
  additionalContent?: string | null;
  carousel?: unknown;
  wideItemList?: unknown;
  video?: unknown;
  commerce?: unknown;
  buttons?: ReadonlyArray<unknown> | null;
  coupon?: unknown;
};

const recordValue = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};

const fieldValue = (value: Record<string, any>, camelKey: string, snakeKey: string) =>
  value[camelKey] ?? value[snakeKey] ?? null;

const linkValue = (value: Record<string, any>) => ({
  linkMobile: fieldValue(value, 'linkMobile', 'link_mobile'),
  linkPc: fieldValue(value, 'linkPc', 'link_pc'),
  linkAndroid: fieldValue(value, 'linkAndroid', 'link_android'),
  linkIos: fieldValue(value, 'linkIos', 'link_ios'),
});

const commerceValue = (value: unknown) => {
  const record = recordValue(value);
  if (!Object.keys(record).length) return null;
  // 고정변수로 저장하면 숫자 필드는 비고 *Name 에 변수명이 들어온다.
  // 이걸 안 보면 수정·복제로 열 때 체크가 풀린 채 가격이 빈 상태로 뜬다.
  const usePriceVariable = Boolean(
    fieldValue(record, 'regularPriceName', 'regular_price_name') ??
      fieldValue(record, 'discountPriceName', 'discount_price_name') ??
      fieldValue(record, 'discountRateName', 'discount_rate_name') ??
      fieldValue(record, 'discountFixedName', 'discount_fixed_name'),
  );
  return {
    title: record.title ?? '',
    regularPrice: fieldValue(record, 'regularPrice', 'regular_price'),
    discountPrice: fieldValue(record, 'discountPrice', 'discount_price'),
    discountRate: fieldValue(record, 'discountRate', 'discount_rate'),
    discountFixed: fieldValue(record, 'discountFixed', 'discount_fixed'),
    usePriceVariable,
  };
};

const reusableWideItemValue = (value: unknown) => {
  const record = recordValue(value);
  return {
    title: record.title ?? '',
    image: null,
    imageUrl: fieldValue(record, 'imageUrl', 'image_url'),
    ...linkValue(record),
  };
};

const reusableCarouselHeadValue = (value: unknown) => {
  const record = recordValue(value);
  return {
    header: record.header ?? null,
    content: record.content ?? null,
    image: null,
    imageUrl: fieldValue(record, 'imageUrl', 'image_url'),
    ...linkValue(record),
  };
};

const reusableCarouselItemValue = (value: unknown) => {
  const record = recordValue(value);
  return {
    header: record.header ?? null,
    content: record.content ?? null,
    image: null,
    imageUrl: fieldValue(record, 'imageUrl', 'image_url'),
    imageLink: fieldValue(record, 'imageLink', 'image_link'),
    additionalContent: fieldValue(record, 'additionalContent', 'additional_content'),
    commerce: commerceValue(record.commerce),
    buttons: record.buttons ?? [],
    coupon: record.coupon ?? null,
  };
};

export const brandMessageTemplateToFormValues = (template: TemplateData): BrandMessageTemplateFormValues => {
  const type = template.chatBubbleType as BrandMessageType;
  const wideItemList = Array.isArray(template.wideItemList) ? template.wideItemList.map(reusableWideItemValue) : [];
  const carousel = recordValue(template.carousel);
  const carouselHead = carousel.head ? reusableCarouselHeadValue(carousel.head) : null;
  const carouselList = Array.isArray(carousel.list) ? carousel.list.map(reusableCarouselItemValue) : [];
  const video = recordValue(template.video);

  return {
    ...initialValuesForType(type),
    templateName: template.templateName,
    chatBubbleType: type,
    adult: template.adult,
    content: template.content,
    image: null,
    imageUrl: template.imageUrl,
    imageLink: template.imageLink,
    header: template.header,
    additionalContent: template.additionalContent,
    buttons: template.buttons ?? [],
    coupon: template.coupon,
    commerce: commerceValue(template.commerce),
    mainWideItem: type === 'WIDE_ITEM_LIST' ? (wideItemList[0] ?? null) : null,
    subWideItemList: type === 'WIDE_ITEM_LIST' ? wideItemList.slice(1) : null,
    carousel:
      type === 'CAROUSEL_FEED' || type === 'CAROUSEL_COMMERCE'
        ? {
            head: carouselHead,
            list: carouselList,
            tail: carousel.tail ? linkValue(recordValue(carousel.tail)) : null,
          }
        : null,
    video: null,
    videoId: video.vid ?? null,
  };
};

type Props = { brandMessageTemplate: BrandMessageTemplateUpdateDialog_brandMessageTemplate$key } & DialogProps &
  Partial<Pick<MutationFormikProps<BrandMessageTemplateUpdateDialog_brandMessageTemplateUpdateMutation>, 'config'>>;

const BrandMessageTemplateUpdateDialog = ({ brandMessageTemplate, config, ...props }: Props) => {
  const template = useFragment(
    BrandMessageTemplateUpdateDialog_brandMessageTemplate,
    brandMessageTemplate,
  ) as TemplateData;

  return (
    <Dialog {...props} wide>
      <Dialog.Header>
        <Text fontSize={2} fontWeight={'bold'}>
          브랜드 메시지 템플릿 수정하기
        </Text>
      </Dialog.Header>
      <MutationFormik<BrandMessageTemplateUpdateDialog_brandMessageTemplateUpdateMutation>
        mutation={graphql`
          mutation BrandMessageTemplateUpdateDialog_brandMessageTemplateUpdateMutation(
            $input: BrandMessageTemplateUpdateInput!
          ) {
            brandMessageTemplateUpdate(input: $input) {
              id
              ...BrandMessageTemplateUpdateDialog_brandMessageTemplate
              parameterNames
              updateTime
              serviceStatus
            }
          }
        `}
        initialValues={{ ...brandMessageTemplateToFormValues(template), templateCode: template.templateCode }}
        validate={validateBrandMessageTemplate}
        valuesTransformer={(values) => normalizeBrandMessageTemplateInput(values)}
        config={config}
      >
        {({ handleSubmit, values, setFieldValue }, { nonFieldErrorRef, scrollContainerRef }) => (
          <BrandMessageTemplateForm
            values={values}
            setFieldValue={setFieldValue}
            handleSubmit={handleSubmit}
            nonFieldErrorRef={nonFieldErrorRef}
            scrollContainerRef={scrollContainerRef}
            submitLabel={'수정하기'}
            typeDisabled
          />
        )}
      </MutationFormik>
    </Dialog>
  );
};

type CloneProps = {
  brandMessageTemplate: BrandMessageTemplateUpdateDialog_brandMessageTemplate$key;
} & React.ComponentProps<typeof BrandMessageTemplateCreateDialog>;

export const BrandMessageTemplateCloneDialog = ({ brandMessageTemplate, ...props }: CloneProps) => {
  const template = useFragment(
    BrandMessageTemplateUpdateDialog_brandMessageTemplate,
    brandMessageTemplate,
  ) as TemplateData;
  return <BrandMessageTemplateCreateDialog {...props} initialValues={brandMessageTemplateToFormValues(template)} />;
};

export default BrandMessageTemplateUpdateDialog;
